import React from 'react'
import type { Block } from '../../types'
import { PropertyLabel, PropertyInput } from './shared'
import { Plus, Trash2, GripVertical } from 'lucide-react'

interface PollPropertiesProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

interface PollOption {
  label: string
  value: string
}

export default function PollProperties({ block, onUpdateBlock }: PollPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const options = (config.options as PollOption[]) || []
  const showResults = (config.showResults as string) || 'after_vote'
  const style = (config.style as string) || 'buttons'

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  const updateOptions = (newOptions: PollOption[]) => {
    updateConfig({ options: newOptions })
  }

  const updateOption = (index: number, field: 'label' | 'value', val: string) => {
    const newOptions = options.map((opt, i) =>
      i === index ? { ...opt, [field]: val } : opt
    )
    updateOptions(newOptions)
  }

  const addOption = () => {
    const num = options.length + 1
    updateOptions([...options, { label: `Option ${num}`, value: `option_${num}` }])
  }

  const removeOption = (index: number) => {
    if (options.length <= 2) return
    updateOptions(options.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Question</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
          placeholder="What do you think?"
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Options</PropertyLabel>
        <div className="space-y-2">
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
              <input
                type="text"
                value={opt.label}
                onChange={(e) => updateOption(i, 'label', e.target.value)}
                placeholder="Label"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
              />
              <button
                onClick={() => removeOption(i)}
                disabled={options.length <= 2}
                className="p-1.5 hover:bg-slate-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Remove option"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addOption}
          className="w-full py-2 border border-dashed border-slate-300 text-slate-500 text-xs font-bold uppercase tracking-wider rounded-lg hover:border-[#5048e5] hover:text-[#5048e5] transition-all flex items-center justify-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Option
        </button>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Show Results</PropertyLabel>
        <div className="grid grid-cols-3 gap-1.5">
          {([
            { value: 'after_vote', label: 'After Vote' },
            { value: 'always', label: 'Always' },
            { value: 'never', label: 'Never' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateConfig({ showResults: opt.value })}
              className={`py-2 px-2 rounded-lg border-2 text-[10px] font-bold uppercase tracking-wider transition-all ${
                showResults === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-400 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Style</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { value: 'buttons', label: 'Buttons' },
            { value: 'bar', label: 'Bar' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateConfig({ style: opt.value })}
              className={`py-2 px-3 rounded-lg border-2 text-xs font-medium capitalize transition-all ${
                style === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {/* Auto-exposed variables */}
      {block.variable && (
        <div className="space-y-2">
          <PropertyLabel>Exposed Variables</PropertyLabel>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
              <span className="font-mono text-[#5048e5]">@{block.variable}_total</span>
              <span className="text-slate-400 ml-auto">total votes</span>
            </div>
            {options.map((opt) => (
              <div key={opt.value} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <span className="font-mono text-[#5048e5]">@{block.variable}_{opt.value}</span>
                <span className="text-slate-400 ml-auto">{opt.label}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400">Use these in text blocks to display live vote data.</p>
        </div>
      )}
    </div>
  )
}
